export const MARKDOWN_TEMPLATE_SYNTAX_VERSION = '1.1.0';

export type MarkdownNumberFormatStyle = 'default' | 'fixed' | 'scientific' | 'percent';

export interface MarkdownValueFormat {
  style: MarkdownNumberFormatStyle;
  digits?: number;
}

export interface MarkdownTemplateToken {
  name: string;
  expression: string;
  start: number;
  end: number;
}

export interface MarkdownTemplateSyntaxIssue {
  code: 'markdown.template_syntax_unsupported' | 'markdown.template_unclosed';
  expression: string;
  start: number;
  end: number;
  message: string;
}

export interface MarkdownTemplateAnalysis {
  tokens: MarkdownTemplateToken[];
  issues: MarkdownTemplateSyntaxIssue[];
}

export const MARKDOWN_SELECTOR_PATTERN = /^[A-Z_]\w*(?:\.[A-Z_]\w*)*$/i;
const unsafeFields = new Set(['__proto__', 'prototype', 'constructor']);

export function isMarkdownSelector(name: string): boolean {
  return MARKDOWN_SELECTOR_PATTERN.test(name) && !name.split('.').some(field => unsafeFields.has(field));
}

/** Literal own JSON-object properties only: no eval, getters or prototypes. */
export function readMarkdownSelector(bindings: Record<string, unknown>, name: string): { value: unknown; missingPath: boolean } {
  if (!isMarkdownSelector(name))
    return { value: undefined, missingPath: true };
  const [root, ...fields] = name.split('.');
  let value: unknown = Object.getOwnPropertyDescriptor(bindings, root!)?.value;
  if (value === undefined || value === null)
    return { value, missingPath: false };
  for (const field of fields) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return { value: undefined, missingPath: true };
    const property = Object.getOwnPropertyDescriptor(value, field);
    if (!property || !Object.hasOwn(property, 'value'))
      return { value: undefined, missingPath: true };
    value = property.value;
  }
  return { value, missingPath: false };
}

/**
 * Analyze one Markdown text node. The caller is responsible for excluding
 * code/pre nodes. Keeping that decision in the Markdown AST traversal avoids
 * treating examples such as `{{value | .2f}}` as executable author syntax.
 */
export function analyzeMarkdownTemplateText(text: string): MarkdownTemplateAnalysis {
  const tokens: MarkdownTemplateToken[] = [];
  const issues: MarkdownTemplateSyntaxIssue[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const start = text.indexOf('{{', cursor);
    if (start < 0)
      break;
    const close = text.indexOf('}}', start + 2);
    if (close < 0) {
      issues.push({
        code: 'markdown.template_unclosed',
        expression: text.slice(start),
        start,
        end: text.length,
        message: '动态占位符缺少结束的 }}；如需展示示例，请使用行内代码或代码块。',
      });
      break;
    }
    const expression = text.slice(start + 2, close).trim();
    if (isMarkdownSelector(expression)) {
      tokens.push({ name: expression, expression, start, end: close + 2 });
    }
    else {
      issues.push({
        code: 'markdown.template_syntax_unsupported',
        expression,
        start,
        end: close + 2,
        message: '动态占位符只支持 {{name}} 或 {{name.field}} 的只读对象字段；显示格式请写入 config.formats，不能执行表达式或读取原型字段。',
      });
    }
    cursor = close + 2;
  }
  return { tokens, issues };
}

/**
 * Analyze executable Markdown text while leaving fenced/inline code and math
 * literal. This deliberately recognizes only boundaries, not a second
 * Markdown renderer; the runtime still performs the final AST-scoped pass.
 */
export function analyzeMarkdownTemplate(content: string): MarkdownTemplateAnalysis {
  const tokens: MarkdownTemplateToken[] = [];
  const issues: MarkdownTemplateSyntaxIssue[] = [];
  let cursor = 0;
  let segmentStart = 0;
  const appendSegment = (end: number) => {
    if (end <= segmentStart)
      return;
    const analysis = analyzeMarkdownTemplateText(content.slice(segmentStart, end));
    tokens.push(...analysis.tokens.map(item => ({ ...item, start: item.start + segmentStart, end: item.end + segmentStart })));
    issues.push(...analysis.issues.map(item => ({ ...item, start: item.start + segmentStart, end: item.end + segmentStart })));
  };
  const escaped = (index: number) => {
    let slashes = 0;
    for (let current = index - 1; current >= 0 && content[current] === '\\'; current--)
      slashes += 1;
    return slashes % 2 === 1;
  };
  while (cursor < content.length) {
    const lineStart = cursor === 0 || content[cursor - 1] === '\n';
    const fence = lineStart ? content.slice(cursor).match(/^[ \t]{0,3}(`{3,}|~{3,})/) : null;
    let delimiter = '';
    let close = -1;
    if (fence) {
      delimiter = fence[1]!;
      const pattern = new RegExp(`^ {0,3}${delimiter[0]}{${delimiter.length},}\\s*$`, 'm');
      const bodyStart = content.indexOf('\n', cursor + fence[0].length);
      const restStart = bodyStart < 0 ? content.length : bodyStart + 1;
      const match = pattern.exec(content.slice(restStart));
      close = match ? restStart + match.index + match[0].length : content.length;
    }
    else if (content[cursor] === '`') {
      const run = content.slice(cursor).match(/^`+/)![0];
      delimiter = run;
      close = content.indexOf(run, cursor + run.length);
      close = close < 0 ? content.length : close + run.length;
    }
    else if (!escaped(cursor) && content.startsWith('\\(', cursor)) {
      delimiter = '\\)';
      close = content.indexOf(delimiter, cursor + 2);
      close = close < 0 ? content.length : close + delimiter.length;
    }
    else if (!escaped(cursor) && content.startsWith('\\[', cursor)) {
      delimiter = '\\]';
      close = content.indexOf(delimiter, cursor + 2);
      close = close < 0 ? content.length : close + delimiter.length;
    }
    else if (!escaped(cursor) && content[cursor] === '$') {
      delimiter = content.startsWith('$$', cursor) ? '$$' : '$';
      let search = cursor + delimiter.length;
      while (search < content.length) {
        const found = content.indexOf(delimiter, search);
        if (found < 0) {
          search = content.length;
          break;
        }
        if (!escaped(found)) {
          search = found + delimiter.length;
          break;
        }
        search = found + delimiter.length;
      }
      close = search;
    }
    if (!delimiter) {
      cursor += 1;
      continue;
    }
    appendSegment(cursor);
    cursor = Math.max(cursor + delimiter.length, close);
    segmentStart = cursor;
  }
  appendSegment(content.length);
  return { tokens, issues };
}

export function isMarkdownFormat(value: unknown): value is MarkdownValueFormat {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return false;
  const candidate = value as Record<string, unknown>;
  if (!['default', 'fixed', 'scientific', 'percent'].includes(String(candidate.style)))
    return false;
  if (candidate.style === 'default')
    return candidate.digits === undefined;
  return Number.isInteger(candidate.digits)
    && Number(candidate.digits) >= 0
    && Number(candidate.digits) <= 12;
}

export function markdownFormatSchemaDescription(): string {
  return 'formats[name] 支持 default，或 fixed/scientific/percent 加 0–12 位 digits；格式仅影响显示，不改变公共科学值。';
}
